import React, { useState, useEffect, useMemo } from 'react';
import './index.css';
import {
  RouterProvider,
  createBrowserRouter,
  Outlet,
  useParams,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { roles } from 'constants/constant.js';
import 'react-datepicker/dist/react-datepicker.css';
import * as Sentry from '@sentry/react';
import { sentry_dsn } from './constants/constant.js';
import { ToastContainer } from 'react-toastify';
import { CloseButton } from 'components/toast';
import 'react-toastify/dist/ReactToastify.css';
import consumerRoutes from 'routes/pages/consumer';
import officerRoutes from 'routes/pages/loan-officer';
import authRoutes from 'routes/pages/auth';
import organizationRoutes from 'routes/pages/organization';
import platformRoutes from 'routes/pages/platform';
import { setUserRole, userRoleData } from 'redux_store/slices/userRole';
import { sessionInitAPI } from 'redux_store/services/sessionInit';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser, fetchCurrentConsumer } from 'services/stytchAuthService';
import {
  stripStatePrefix,
  getCurrentStateId,
  persistStateId,
  buildPathForState,
} from 'utils/stateRouting';
import usePageTitle from 'constants/PageTitle';
import { CheckAuth, clearCachedUserMetadata } from 'auth/auth';
import './setup/reactSelectDefaults';
import Loader from 'wrapper/loader';

if (!globalThis.__SENTRY_INITIALIZED__) {
  Sentry.init({
    dsn: sentry_dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  globalThis.__SENTRY_INITIALIZED__ = true;
}

//used to update title of web app as per DEV, UAT and production build
document.title = import.meta.env.VITE_APP_TITLE;

const normalizeRouteDefinitions = (routes = []) =>
  routes.map((route) => {
    const normalized = { ...route };

    if (typeof normalized.path === 'string') {
      if (normalized.path === '*') {
        // keep wildcard as-is
      } else if (normalized.path.startsWith('/')) {
        const trimmed = normalized.path.replace(/^\/+/, '');
        normalized.path = trimmed;
      }

      if (normalized.path === '') {
        delete normalized.path;
      }
    }

    if (normalized.children) {
      normalized.children = normalizeRouteDefinitions(normalized.children);
    }

    return normalized;
  });

const StateRouterBoundary = () => {
  const { stateId } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (stateId) {
      persistStateId(stateId);
    }
  }, [stateId]);

  if (!stateId) {
    const fallback = getCurrentStateId();
    if (fallback) {
      const normalizedPath = stripStatePrefix(location.pathname || '/');
      const targetPath = buildPathForState(fallback, normalizedPath || '/');
      const targetUrl = `${targetPath}${location.search || ''}`;
      return <Navigate to={targetUrl} replace state={location.state} />;
    }
  }

  return <Outlet />;
};

const wrapRoutesWithState = (routes) => [
  {
    path: '/:stateId?',
    element: <StateRouterBoundary />,
    children: normalizeRouteDefinitions(routes),
  },
];

const routerOptions = {};

const authRouter = createBrowserRouter(
  [...platformRoutes, ...wrapRoutesWithState([...authRoutes])],
  routerOptions
);

const resolveRoleFromUser = (user) => {
  if (!user) {
    return null;
  }

  const backendRoleRaw =
    user.originalRole || user.role || user.original_role || '';
  const normalizedRole = String(backendRoleRaw).toLowerCase();

  if (normalizedRole.includes('platform')) {
    return roles.superAdmin;
  }
  if (normalizedRole.includes('consumer')) {
    return roles.consumer;
  }
  if (
    normalizedRole === 'client_admin' ||
    normalizedRole.includes('client') ||
    normalizedRole.includes('org') ||
    normalizedRole === 'admin'
  ) {
    return roles.organization;
  }
  if (
    normalizedRole === 'branch_admin' ||
    normalizedRole.includes('branch')
  ) {
    return roles.branchAdmin;
  }
  if (normalizedRole === 'loan_officer') {
    return roles.loanOfficer;
  }
  if (normalizedRole === 'loan_processor') {
    return roles.loanProcessor;
  }

  return null;
};

const AppRouter = ({ isRoute }) => {
  const role = useSelector(userRoleData);

  const router = useMemo(() => {
    const userRole = role?.userRole;

    if (userRole === roles.consumer) {
      return createBrowserRouter([...platformRoutes, ...wrapRoutesWithState([...consumerRoutes])], routerOptions);
    }
    if (userRole === roles.loanOfficer || userRole === roles.loanProcessor) {
      return createBrowserRouter([...platformRoutes, ...wrapRoutesWithState([...officerRoutes])], routerOptions);
    }
    if (userRole === roles.organization || userRole === roles.branchAdmin || userRole === roles.superAdmin) {
      return createBrowserRouter([...platformRoutes, ...wrapRoutesWithState([...organizationRoutes])], routerOptions);
    }
    return createBrowserRouter([...platformRoutes, ...wrapRoutesWithState([...authRoutes])], routerOptions);
  }, [role?.userRole]);

  return isRoute ? <RouterProvider router={router} future={{ v7_startTransition: true }} /> : <></>;
};

const App = () => {
  usePageTitle('');
  const dispatch = useDispatch();
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  // Track whether the current bootstrap already made a fresh /auth/me call,
  // so we can skip the redundant background validation.
  const freshAuthCallRef = React.useRef(false);

  useEffect(() => {
    const bootstrap = async () => {
      // Skip bootstrap when landing on auth callback or login URLs.
      // - Callback pages handle their own token exchange
      // - Login pages must render the login form, not auto-redirect to
      //   a cached user's dashboard (enables switching accounts)
      const pathname = window.location.pathname || '';
      const isAuthPage =
        pathname.includes('/auth/callback') ||
        pathname.includes('/platform/auth/callback') ||
        pathname.includes('/login') ||
        pathname.includes('/stytch-login');
      if (isAuthPage) {
        setIsAuthChecking(false);
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      const isB2CFlow = searchParams.get('flow') === 'b2c';

      try {
        // Prefer B2C auth when explicitly requested - even if we already have cached metadata.
        if (isB2CFlow) {
          try {
            const consumerResponse = await fetchCurrentConsumer();
            const consumerUser = consumerResponse?.data;

            if (consumerUser) {
              freshAuthCallRef.current = true;
              clearCachedUserMetadata();
              const { cacheUserMetadata } = await import('auth/auth');
              cacheUserMetadata(consumerUser, {
                permissions: consumerUser.permissions || [],
              });
              dispatch(setUserRole(roles.consumer));
              dispatch(sessionInitAPI.endpoints.getSessionInit.initiate());
              setIsAuthChecking(false);
              return;
            }
          } catch (consumerError) {
            // B2C auth failed - fall through to B2B check
            console.warn('[App] B2C auth bootstrap failed', consumerError);
          }
        }

        // Check for cached user next - provides instant UI if available
        const cachedUser = CheckAuth();
        const cachedRole = resolveRoleFromUser(cachedUser);

        if (cachedRole) {
          dispatch(setUserRole(cachedRole));
          dispatch(sessionInitAPI.endpoints.getSessionInit.initiate());
          setIsAuthChecking(false);
          // Validate session in background - don't block UI
          validateSessionInBackground();
          return;
        }

        // No cached user - need to check with backend
        // Try B2B /auth/me endpoint (works for all B2B roles)
        try {
          freshAuthCallRef.current = true;
          const response = await fetchCurrentUser();
          const payload = response?.data?.data ?? response?.data ?? {};
          const user = payload.user || payload.current_user;

          if (user) {
            const { cacheUserMetadata } = await import('auth/auth');
            cacheUserMetadata(user, { permissions: user.permissions || [] });

            const resolvedRole = resolveRoleFromUser(user);
            if (resolvedRole) {
              dispatch(setUserRole(resolvedRole));
              dispatch(sessionInitAPI.endpoints.getSessionInit.initiate());
            }
          }
        } catch (authError) {
          const status = authError?.response?.status;
          if (status !== 401) {
            console.warn('[App] Auth bootstrap via /auth/me failed', authError);
          }
          // 401 is expected for unauthenticated users - just show login
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401) {
          clearCachedUserMetadata();
        } else {
          console.warn('[App] Auth bootstrap failed', error);
        }
      } finally {
        setIsAuthChecking(false);
      }
    };

    // Background session validation - called when we have cached user
    const validateSessionInBackground = async () => {
      // Skip if we already made a fresh auth call during this bootstrap
      if (freshAuthCallRef.current) {
        return;
      }
      try {
        const response = await fetchCurrentUser();
        const payload = response?.data?.data ?? response?.data ?? {};
        const user = payload.user || payload.current_user;

        if (user) {
          const { cacheUserMetadata } = await import('auth/auth');
          cacheUserMetadata(user, { permissions: user.permissions || [] });
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401) {
          // Session expired - clear cache and reset role
          clearCachedUserMetadata();
          dispatch(setUserRole(null));
        }
      }
    };

    bootstrap();

    // Listen for cross-tab session events.  When another tab logs in as a
    // different user or logs out, we respond by cleaning up and redirecting.
    const onStorageChange = (e) => {
      if (e.key === 'fm_cross_tab_event' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          if (data.type === 'logout' || data.type === 'secondary_login') {
            // Another tab logged out or a different user logged in —
            // this tab's session is no longer valid (cookie was overwritten
            // or session revoked in Redis). Redirect to login.
            clearCachedUserMetadata();
            dispatch(setUserRole(null));
            sessionStorage.clear();
            window.location.href = window.location.origin;
          }
        } catch { /* ignore parse errors */ }
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, [dispatch]);

  const role = useSelector(userRoleData);

  // Show branded loading screen while checking authentication
  if (isAuthChecking) {
    return <Loader variant="colorful" FullScreen={true} message="Signing in..." />;
  }

  // If the user is authenticated (has a role), render only the
  // application router and global toasts. Skip the promotional
  // "Let's Begin" shell to avoid UI duplication inside the app.
  if (role?.userRole) {
    return (
      <>
        <AppRouter isRoute={true} />
        <ToastContainer
          position="bottom-center"
          autoClose={3000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          closeButton={CloseButton}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </>
    );
  }

  return (
    <>
      <RouterProvider router={authRouter} future={{ v7_startTransition: true }} />
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        closeButton={CloseButton}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default App;
